import { useState, useEffect } from 'react';
import { mcqAPI, chapterAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import {
  RiAddLine, RiDeleteBinLine, RiQuestionLine, RiCloseLine,
  RiLoaderLine, RiLinksLine, RiToggleLine, RiToggleFill,
  RiEditLine, RiCheckLine, RiTimeLine, RiPercentLine,
} from 'react-icons/ri';

const MCQManagement = () => {
  const [mcqs, setMcqs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', chapterId: '', duration: 30, passingPercentage: 40 });
  const [questions, setQuestions] = useState([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState({
    questionText: '',
    options: [
      { optionLetter: 'A', optionText: '' },
      { optionLetter: 'B', optionText: '' },
      { optionLetter: 'C', optionText: '' },
      { optionLetter: 'D', optionText: '' },
    ],
    correctAnswer: 'A',
  });
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (showForm) loadChapters(); }, [showForm]);

  const fetchData = async () => {
    try {
      const res = await mcqAPI.getAllMCQs();
      setMcqs(res.data.mcqs || []);
    } catch {
      toast.error('Failed to load MCQ tests');
    } finally {
      setLoading(false);
    }
  };

  const loadChapters = async () => {
    setChaptersLoading(true);
    try {
      const res = await chapterAPI.getAllChapters();
      setChapters(res.data.chapters || []);
    } catch {
      setChapters([]);
    } finally {
      setChaptersLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: (name === 'duration' || name === 'passingPercentage') ? parseInt(value) : value }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index].optionText = value;
    setCurrentQuestion(prev => ({ ...prev, options: newOptions }));
  };

  const handleAddQuestion = () => {
    if (!currentQuestion.questionText.trim()) { toast.warning('Please enter question text'); return; }
    if (currentQuestion.options.some(o => !o.optionText.trim())) { toast.warning('Please fill all 4 options'); return; }
    if (editingIndex !== null) {
      const updated = [...questions];
      updated[editingIndex] = { ...currentQuestion, questionNumber: editingIndex + 1 };
      setQuestions(updated);
      setEditingIndex(null);
    } else {
      setQuestions(prev => [...prev, { ...currentQuestion, questionNumber: prev.length + 1 }]);
    }
    setCurrentQuestion({ questionText: '', options: [{ optionLetter: 'A', optionText: '' }, { optionLetter: 'B', optionText: '' }, { optionLetter: 'C', optionText: '' }, { optionLetter: 'D', optionText: '' }], correctAnswer: 'A' });
    setShowQuestionForm(false);
  };

  const handleEditQuestion = (index) => {
    setCurrentQuestion(questions[index]);
    setEditingIndex(index);
    setShowQuestionForm(true);
  };

  const handleDeleteQuestion = (index) => setQuestions(questions.filter((_, i) => i !== index));

  const resetForm = () => {
    setFormData({ title: '', description: '', chapterId: '', duration: 30, passingPercentage: 40 });
    setQuestions([]);
    setCurrentQuestion({ questionText: '', options: [{ optionLetter: 'A', optionText: '' }, { optionLetter: 'B', optionText: '' }, { optionLetter: 'C', optionText: '' }, { optionLetter: 'D', optionText: '' }], correctAnswer: 'A' });
    setShowQuestionForm(false);
    setEditingIndex(null);
    setShowForm(false);
  };

  const handleSubmitMCQ = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.warning('Please enter MCQ title'); return; }
    if (!formData.chapterId) { toast.warning('Please select a chapter'); return; }
    if (questions.length === 0) { toast.warning('Please add at least one question'); return; }
    setIsSubmitting(true);
    try {
      await mcqAPI.createMCQ({ ...formData, questions });
      toast.success('MCQ test created successfully');
      resetForm();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create MCQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyShareLink = (shareCode) => {
    const link = `${window.location.origin}/mcq/${shareCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Share link copied to clipboard');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this MCQ?')) return;
    try {
      await mcqAPI.deleteMCQ(id);
      toast.success('MCQ deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete MCQ');
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await mcqAPI.togglePublish(id);
      fetchData();
    } catch {
      toast.error('Failed to update MCQ');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 spinner" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">MCQ Tests</h1>
          <p className="text-sm text-slate-500 mt-0.5">{mcqs.length} test{mcqs.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* MCQ cards */}
      {mcqs.length === 0 ? (
        <div className="card p-12 text-center">
          <RiQuestionLine className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No MCQ tests yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first test to share with students</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {mcqs.map(mcq => (
            <div key={mcq._id} className="card-hover p-5 flex flex-col animate-fade-in-up">
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="font-semibold text-slate-800 line-clamp-2 flex-1">{mcq.title}</h3>
                <span className={`badge flex-shrink-0 ${mcq.isPublished ? 'badge-green' : 'badge-yellow'}`}>
                  {mcq.isPublished ? 'Live' : 'Draft'}
                </span>
              </div>
              {mcq.description && <p className="text-sm text-slate-500 line-clamp-2 mb-3">{mcq.description}</p>}
              <div className="grid grid-cols-3 gap-2 mb-4 text-xs text-slate-500">
                <div className="flex flex-col items-center gap-1 bg-slate-50 rounded-lg p-2">
                  <RiQuestionLine className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-semibold text-slate-700">{mcq.totalQuestions}</span>
                  <span>Questions</span>
                </div>
                <div className="flex flex-col items-center gap-1 bg-slate-50 rounded-lg p-2">
                  <RiTimeLine className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-semibold text-slate-700">{mcq.duration}</span>
                  <span>Minutes</span>
                </div>
                <div className="flex flex-col items-center gap-1 bg-slate-50 rounded-lg p-2">
                  <RiPercentLine className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-semibold text-slate-700">{mcq.passingPercentage}</span>
                  <span>Pass %</span>
                </div>
              </div>
              <div className="mt-auto flex flex-col gap-2">
                <button onClick={() => handleCopyShareLink(mcq.shareCode)} className="btn-secondary w-full text-xs">
                  <RiLinksLine className="w-3.5 h-3.5" /> Copy Share Link
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleTogglePublish(mcq._id)}
                    className={`flex-1 btn text-xs ${mcq.isPublished ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 focus:ring-amber-400' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 focus:ring-emerald-400'}`}
                  >
                    {mcq.isPublished ? <><RiToggleFill className="w-3.5 h-3.5" /> Unpublish</> : <><RiToggleLine className="w-3.5 h-3.5" /> Publish</>}
                  </button>
                  <button onClick={() => handleDelete(mcq._id)} className="btn bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 text-xs flex-1 focus:ring-red-400">
                    <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create MCQ Modal */}
      {showForm && (
        <div className="modal-overlay animate-fade-in">
          <div className="modal-box w-full max-w-3xl max-h-[92vh] flex flex-col animate-scale-in">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-slate-800">Create MCQ Test</h2>
              <button onClick={resetForm} className="btn-icon btn-ghost text-slate-400">
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitMCQ} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Test details */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Test Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Title *</label>
                      <input className="input" type="text" name="title" value={formData.title} onChange={handleFormChange} placeholder="e.g. Chapter 1 Quiz" />
                    </div>
                    <div>
                      <label className="label">Chapter *</label>
                      <select className="input" name="chapterId" value={formData.chapterId} onChange={handleFormChange} disabled={chaptersLoading}>
                        <option value="">{chaptersLoading ? 'Loading…' : 'Select a chapter'}</option>
                        {chapters.map(ch => <option key={ch._id} value={ch._id}>{ch.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label">Duration (minutes)</label>
                      <input className="input" type="number" name="duration" value={formData.duration} onChange={handleFormChange} min="1" />
                    </div>
                    <div>
                      <label className="label">Passing Percentage</label>
                      <input className="input" type="number" name="passingPercentage" value={formData.passingPercentage} onChange={handleFormChange} min="0" max="100" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="label">Description</label>
                      <textarea className="input resize-none" name="description" value={formData.description} onChange={handleFormChange} rows={2} placeholder="Optional description" />
                    </div>
                  </div>
                </div>

                {/* Questions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                      Questions <span className="text-blue-600 font-bold">({questions.length})</span>
                    </h3>
                    {!showQuestionForm && (
                      <button type="button" onClick={() => setShowQuestionForm(true)} className="btn-success btn-sm">
                        <RiAddLine className="w-3.5 h-3.5" /> Add Question
                      </button>
                    )}
                  </div>

                  {/* Question form */}
                  {showQuestionForm && (
                    <div className="card p-5 mb-4 border-l-4 border-blue-500 animate-fade-in-up">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">
                        {editingIndex !== null ? 'Edit Question' : 'New Question'}
                      </h4>
                      <div className="mb-4">
                        <label className="label">Question Text *</label>
                        <textarea
                          className="input resize-none"
                          value={currentQuestion.questionText}
                          onChange={e => setCurrentQuestion(p => ({ ...p, questionText: e.target.value }))}
                          rows={2}
                          placeholder="Enter the question…"
                        />
                      </div>
                      <div className="mb-4">
                        <label className="label">Options * — select the correct answer</label>
                        <div className="space-y-2">
                          {currentQuestion.options.map((opt, idx) => (
                            <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${currentQuestion.correctAnswer === opt.optionLetter ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                              <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                                <input
                                  type="radio"
                                  name="correct"
                                  value={opt.optionLetter}
                                  checked={currentQuestion.correctAnswer === opt.optionLetter}
                                  onChange={e => setCurrentQuestion(p => ({ ...p, correctAnswer: e.target.value }))}
                                  className="w-4 h-4 accent-emerald-600"
                                />
                                <span className={`text-xs font-bold w-5 ${currentQuestion.correctAnswer === opt.optionLetter ? 'text-emerald-600' : 'text-slate-500'}`}>
                                  {opt.optionLetter}
                                </span>
                              </label>
                              <input
                                type="text"
                                value={opt.optionText}
                                onChange={e => handleOptionChange(idx, e.target.value)}
                                className="flex-1 text-sm bg-transparent border-none outline-none placeholder-slate-400 text-slate-700"
                                placeholder={`Option ${opt.optionLetter}`}
                              />
                              {currentQuestion.correctAnswer === opt.optionLetter && (
                                <RiCheckLine className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button type="button" onClick={handleAddQuestion} className="btn-primary flex-1 btn-sm">
                          {editingIndex !== null ? 'Update Question' : 'Add Question'}
                        </button>
                        <button type="button" onClick={() => { setShowQuestionForm(false); setEditingIndex(null); }} className="btn-secondary flex-1 btn-sm">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Questions list */}
                  {questions.length > 0 && (
                    <div className="space-y-2">
                      {questions.map((q, idx) => (
                        <div key={idx} className="card p-4 group">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-start gap-2">
                              <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {q.questionNumber}
                              </span>
                              <p className="text-sm font-medium text-slate-700">{q.questionText}</p>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                              <button type="button" onClick={() => handleEditQuestion(idx)} className="btn-icon btn-ghost text-blue-600 hover:bg-blue-50">
                                <RiEditLine className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" onClick={() => handleDeleteQuestion(idx)} className="btn-icon btn-ghost text-red-600 hover:bg-red-50">
                                <RiDeleteBinLine className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-1 ml-8">
                            {q.options.map(opt => (
                              <div key={opt.optionLetter} className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${opt.optionLetter === q.correctAnswer ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                                <span className="font-bold">{opt.optionLetter}.</span> {opt.optionText}
                                {opt.optionLetter === q.correctAnswer && <RiCheckLine className="w-3 h-3 ml-auto" />}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {questions.length === 0 && !showQuestionForm && (
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                      <RiQuestionLine className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">No questions yet. Add your first question above.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                <button type="submit" disabled={isSubmitting || questions.length === 0} className="btn-success flex-1">
                  {isSubmitting ? <><RiLoaderLine className="w-4 h-4 animate-spin" /> Creating…</> : 'Create MCQ Test'}
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCQManagement;
